import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import JobDetailsModal from "@/components/JobDetailsModal";
import AdvancedJobDetailsModalForCrew from "@/components/AdvancedJobDetailsModalForCrew";
import {
  Calendar, CreditCard, DollarSign,
  Search, Clock, CheckCircle, AlertCircle, Users, Eye, RefreshCw
} from "lucide-react";
import { toast } from 'sonner';

const AdminUpcomingPayments = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming'); // upcoming or completed
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summary, setSummary] = useState({ upcoming: 0, completed: 0, total_upcoming_amount: 0 });
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [retryingPayment, setRetryingPayment] = useState(null);
  const paymentsPerPage = 20; // Increased to show more payments by default

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');

    setPage(1);
    setPayments([]);
    fetchPayments(1);
    fetchSummary();
  }, [activeTab, searchTerm]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/summary`
      );

      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchPayments = async (pageNum) => {
    try {
      const isLoadingMore = pageNum > 1;
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/${activeTab}`,
        {
          params: {
            page: pageNum,
            limit: paymentsPerPage,
            search: searchTerm || undefined
          }
        }
      );

      if (response.data.success) {
        const newPayments = response.data.payments || [];

        if (isLoadingMore) {
          setPayments(prev => [...prev, ...newPayments]);
        } else {
          setPayments(newPayments);
        }

        setHasMore(newPayments.length === paymentsPerPage);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    fetchPayments(page + 1);
  };

  const handleViewJob = async (payment) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${payment.job_id}`
      );

      if (response.data) {
        setSelectedJob(response.data);
        setShowJobDetails(true);
      }
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    }
  };

  const handleRetryPayment = async (payment) => {
    const confirmed = window.confirm(
      `Retry payment of ${formatCurrency(payment.amount)} for "${payment.job_title}"?\n\n` +
      `Client: ${payment.client_name}\n` +
      `Card: ${payment.card_brand} •••• ${payment.card_last4}\n` +
      `Invoice: ${payment.invoice_number}\n\n` +
      `This will attempt to charge the card immediately.`
    );
    if (!confirmed) return;

    setRetryingPayment(payment.invoice_number);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/retry`,
        { invoice_number: payment.invoice_number }
      );

      if (response.data.success) {
        toast.success(`Payment of ${formatCurrency(response.data.amount)} captured successfully`);
        setPage(1);
        setPayments([]);
        fetchPayments(1);
        fetchSummary();
      } else {
        toast.error(response.data.message || 'Payment failed');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Retry failed';
      toast.error(`Retry failed: ${msg}`);
    } finally {
      setRetryingPayment(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isAdvancedJob = (job) => {
    return (job.crew_members && job.crew_members.length > 1) ||
           (job.dates && job.dates.length > 1) ||
           (job.crew_count && job.crew_count > 1);
  };

  // ---- Summary cards (Upcoming / Total Scheduled $ / Completed) ----
  const summaryCards = [
    {
      label: 'Upcoming',
      value: summary.upcoming,
      Icon: Clock,
      chipBg: '#FEF3C7',
      chipFg: '#B45309',
    },
    {
      label: 'Total Scheduled',
      value: formatCurrency(summary.total_upcoming_amount),
      Icon: DollarSign,
      chipBg: '#DBEAFE',
      chipFg: '#2563EB',
    },
    {
      label: 'Completed',
      value: summary.completed,
      Icon: CheckCircle,
      chipBg: '#DCFCE7',
      chipFg: '#15803D',
    },
  ];

  const tabs = [
    { key: 'upcoming', label: 'Upcoming', count: summary.upcoming },
    { key: 'completed', label: 'Completed', count: summary.completed },
  ];

  // Status pill styling
  const statusStyle = (status) => {
    if (status === 'paid') return { label: 'Paid', fg: '#15803D', bg: '#DCFCE7' };
    if (status === 'overdue') return { label: 'Overdue', fg: '#B91C1C', bg: '#FEE2E2' };
    return { label: 'Scheduled', fg: '#2563EB', bg: '#DBEAFE' };
  };

  return (
    <div className="min-h-full">
      {/* Sticky Header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div>
          <h1 className="sz-h1 m-0">Upcoming Payments</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">Scheduled client charges &amp; payouts</div>
        </div>
      </header>

      {/* Scroll body */}
      <main className="px-[30px] pt-[22px] pb-[56px] max-w-[1080px]">
        {/* Summary cards */}
        <div
          className="grid gap-[14px]"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
        >
          {summaryCards.map((c) => {
            const Icon = c.Icon;
            return (
              <div key={c.label} className="sz-card p-[18px]">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-[#8A90A2]">{c.label}</span>
                  <div
                    className="w-8 h-8 rounded-[9px] flex items-center justify-center"
                    style={{ background: c.chipBg, color: c.chipFg }}
                  >
                    <Icon className="w-[17px] h-[17px]" />
                  </div>
                </div>
                <div className="sz-stat-num text-[24px] mt-[10px]">{c.value}</div>
              </div>
            );
          })}
        </div>

        {/* Search + tabs */}
        <div className="flex items-center gap-3 mt-[18px] flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8AEBC]" />
            <Input
              type="text"
              placeholder="Search job, client or invoice…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sz-input pl-9"
            />
          </div>
          <div className="flex gap-[5px] bg-white border border-[#E6E9EF] p-[5px] rounded-[11px]">
            {tabs.map((t) => {
              const on = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold transition-colors"
                  style={{
                    color: on ? '#fff' : '#8A90A2',
                    background: on ? '#0C1120' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Payments list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-[#E6E9EF] border-t-[#78287C]"></div>
            <p className="mt-4 text-[#8A90A2] text-sm">Loading payments…</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="sz-card p-12 text-center mt-4">
            <Calendar className="w-12 h-12 text-[#A8AEBC] mx-auto mb-4" />
            <p className="sz-h3 text-[#2B3142]">
              {activeTab === 'upcoming' ? 'No upcoming payments scheduled' : 'No completed payments found'}
            </p>
            {searchTerm && (
              <p className="text-sm text-[#8A90A2] mt-2">Try adjusting your search</p>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-[11px] mt-4">
              {payments.map((payment) => {
                const daysUntil = getDaysUntil(payment.payment_date);
                const isPast = activeTab === 'upcoming' && daysUntil !== null && daysUntil < 0;
                const isPaid = activeTab === 'completed';
                const status = isPaid ? 'paid' : isPast ? 'overdue' : 'scheduled';
                const st = statusStyle(status);

                // Icon tile: card-brand tile (checkmark when paid)
                const iconBg = isPaid ? '#DCFCE7' : isPast ? '#FEE2E2' : '#EDE4F4';
                const iconFg = isPaid ? '#15803D' : isPast ? '#B91C1C' : '#78287C';
                const TileIcon = isPaid ? CheckCircle : CreditCard;

                // Date label + value
                const dateLabel = activeTab === 'upcoming' ? 'Scheduled' : 'Paid';
                const dateFg = isPast ? '#B91C1C' : '#2B3142';
                let dateValue = formatDate(payment.payment_date);
                if (activeTab === 'upcoming' && daysUntil !== null) {
                  const rel = isPast
                    ? `overdue · ${Math.abs(daysUntil)}d`
                    : daysUntil === 0
                    ? 'today'
                    : `in ${daysUntil}d`;
                  dateValue = `${rel} · ${formatDate(payment.payment_date)}`;
                }

                const method = `${payment.card_brand || 'Card'} ···${payment.card_last4 || '••••'}`;

                return (
                  <div
                    key={payment.invoice_id}
                    className="sz-card sz-card-hover sz-fade p-[15px_18px] flex items-center gap-4 flex-wrap"
                  >
                    {/* Card-brand icon tile */}
                    <div
                      className="w-[42px] h-[42px] rounded-[11px] flex items-center justify-center flex-none"
                      style={{ background: iconBg, color: iconFg }}
                    >
                      <TileIcon className="w-5 h-5" />
                    </div>

                    {/* Job + invoice / client + method */}
                    <div className="flex-1 min-w-[160px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13.5px] font-semibold text-[#0C1120]">{payment.job_title}</span>
                        <span className="text-[10.5px] text-[#A8AEBC]" style={{ fontFamily: 'Poppins' }}>
                          {payment.invoice_number}
                        </span>
                      </div>
                      <div className="text-[12px] text-[#8A90A2] mt-[3px]">
                        {payment.client_name} · {method}
                      </div>
                    </div>

                    {/* Scheduled / paid date */}
                    <div className="text-right min-w-[140px]">
                      <div className="text-[11px] text-[#8A90A2]">{dateLabel}</div>
                      <div className="text-[12.5px] font-semibold" style={{ color: dateFg }}>
                        {dateValue}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right min-w-[90px]">
                      <div className="sz-stat-num text-[16px]">{formatCurrency(payment.amount)}</div>
                    </div>

                    {/* Status pill */}
                    <div className="flex-none">
                      <span
                        className="text-[11px] font-bold px-[11px] py-[5px] rounded-full"
                        style={{ fontFamily: 'Poppins', color: st.fg, background: st.bg }}
                      >
                        {st.label}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-none ml-auto">
                      <button
                        onClick={() => handleViewJob(payment)}
                        className="sz-btn sz-btn-outline"
                        style={{ padding: '7px 12px', fontSize: '12.5px' }}
                      >
                        <Eye className="w-4 h-4" />
                        View Job
                      </button>
                      {status === 'overdue' && (
                        <button
                          onClick={() => handleRetryPayment(payment)}
                          disabled={retryingPayment === payment.invoice_number}
                          className="sz-btn sz-btn-danger"
                          style={{ padding: '7px 12px', fontSize: '12.5px' }}
                        >
                          {retryingPayment === payment.invoice_number ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"></div>
                              Retrying…
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              Retry Payment
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load more */}
            {hasMore && !loading && (
              <div className="text-center mt-[18px]">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="sz-btn sz-btn-outline"
                  style={{ padding: '11px 26px' }}
                >
                  {loadingMore ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-[#E6E9EF] border-t-[#78287C]"></div>
                      Loading…
                    </>
                  ) : (
                    <>Load more</>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Job Details Modal */}
      {showJobDetails && selectedJob && (
        isAdvancedJob(selectedJob) ? (
          <AdvancedJobDetailsModalForCrew
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => {
              setShowJobDetails(false);
              setSelectedJob(null);
            }}
          />
        ) : (
          <JobDetailsModal
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => {
              setShowJobDetails(false);
              setSelectedJob(null);
            }}
            showApplyButton={false}
          />
        )
      )}
    </div>
  );
};

export default AdminUpcomingPayments;
